import { useFavouriteWellsManager } from 'hooks/useFavouriteWellsManager';
import { useFetchWellsWithWellbores } from 'hooks/useFetchWellsWithWellbores';
import { useMemo } from 'react';
import { WellFullDetailsWithUniqueKeyDTO } from 'types/well';

export type CategorizedWells = {
  favouriteWells: WellFullDetailsWithUniqueKeyDTO[];
  activeSensorWells: WellFullDetailsWithUniqueKeyDTO[];
  activeWellviewWells: WellFullDetailsWithUniqueKeyDTO[];
  nonActiveWells: WellFullDetailsWithUniqueKeyDTO[];
  wellsByCountry: WellFullDetailsWithUniqueKeyDTO[];
};

type ReturnTypeWithoutFavourites = {
  categorizedWells: {
    activeSensorWells: WellFullDetailsWithUniqueKeyDTO[];
    activeWellviewWells: WellFullDetailsWithUniqueKeyDTO[];
    nonActiveWells: WellFullDetailsWithUniqueKeyDTO[];
  };
  fetchWells: () => Promise<void>;
  allWellsLoading: boolean;
};

export type ReturnTypeWithFavourites = {
  categorizedWells: CategorizedWells;
};

export type UseFetchAllCategorizedWells = {
  (extractFavourites?: false): ReturnTypeWithoutFavourites;
  (extractFavourites: true): ReturnTypeWithFavourites;
};

const NUMBER_OF_DAYS_TO_CONSIDER_SENSOR_WELL_AS_ACTIVE = 5;

export const useFetchAllCategorizedWells: UseFetchAllCategorizedWells = (
  extractFavourites = false,
) => {
  const {
    fetchWells,
    wells: authorizedWells,
    wellsAndWellboresLoading,
  }: {
    wells: WellFullDetailsWithUniqueKeyDTO[];
    fetchWells: () => Promise<void>;
    wellsAndWellboresLoading: boolean;
  } = useFetchWellsWithWellbores();

  const { isFavourite } = useFavouriteWellsManager();

  const categorizedWells = useMemo(() => {
    try {
      const favouriteWells: WellFullDetailsWithUniqueKeyDTO[] = [];
      const activeSensorWells: WellFullDetailsWithUniqueKeyDTO[] = [];
      const activeWellviewWells: WellFullDetailsWithUniqueKeyDTO[] = [];
      const nonActiveWells: WellFullDetailsWithUniqueKeyDTO[] = [];
      const wellsByCountry: WellFullDetailsWithUniqueKeyDTO[] = [];

      const endDateToConsiderSensorWellAsActive = new Date();
      endDateToConsiderSensorWellAsActive.setDate(
        endDateToConsiderSensorWellAsActive.getDate() -
          NUMBER_OF_DAYS_TO_CONSIDER_SENSOR_WELL_AS_ACTIVE,
      );

      if (!authorizedWells || authorizedWells.length === 0) {
        return {
          favouriteWells,
          activeSensorWells,
          activeWellviewWells,
          nonActiveWells,
          wellsByCountry,
        };
      }

      authorizedWells.forEach(well => {
        const is_favorite = isFavourite(well) || false;
        if (extractFavourites && is_favorite) {
          favouriteWells.push(well);
        } else if (well.isLive) {
          activeSensorWells.push(well);
          wellsByCountry.push(well);
        } else if (well.isReporting) {
          activeWellviewWells.push(well);
          wellsByCountry.push(well);
        } else {
          nonActiveWells.push(well);
          wellsByCountry.push(well);
        }
      });

      return {
        favouriteWells,
        activeSensorWells,
        activeWellviewWells,
        nonActiveWells,
        wellsByCountry,
      };
    } catch (error) {
      throw error;
    }
  }, [authorizedWells, extractFavourites, isFavourite]);

  return { categorizedWells, fetchWells, allWellsLoading: wellsAndWellboresLoading };
};
